import React from "react";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  LockOutlined,
  MailOutlined,
  SafetyOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import Navbar from "../component/homePage/navbar";
import Footer from "../component/homePage/footer";

type PolicySection = {
  number: string;
  title: string;
  content?: React.ReactNode;
};

const PrivacyPolicy = () => {
  const lastUpdated = "August 18, 2026";

  const informationCollected = [
    {
      title: "Account information",
      description:
        "Name, email address, role and other account information provided by authorized users when they sign in to KGS Studio.",
    },
    {
      title: "Google account information",
      description:
        "Basic profile and email information provided by Google when a user authenticates with Google and authorizes KGS Studio.",
    },
    {
      title: "YouTube account and channel information",
      description:
        "Information associated with the YouTube channel that the user explicitly authorizes KGS Studio to access, such as channel identifiers, channel title and information required to perform authorized publishing and live-stream operations.",
    },
    {
      title: "Video information and metadata",
      description:
        "Video identifiers, titles, descriptions, privacy or publishing status, scheduled publishing information and processing status associated with videos managed through KGS Studio.",
    },
    {
      title: "Uploaded video files",
      description:
        "Video files submitted by authorized users through KGS Studio for uploading or publishing to their authorized YouTube channel.",
    },
    {
      title: "Live-stream information",
      description:
        "Information related to authorized live broadcasts and stream resources, including broadcast lifecycle status, stream configuration and monitoring information required to create, start, monitor and end broadcasts.",
    },
    {
      title: "Operational information",
      description:
        "Class, course, lecture, scheduling and publishing information entered into KGS Studio by authorized users.",
    },
    {
      title: "Technical and log information",
      description:
        "IP address, browser or device information, request logs, job status, error logs and related technical information used to operate, secure and troubleshoot KGS Studio.",
    },
  ];

  const youtubeData = [
    "YouTube channel identifiers and channel information required to identify the authorized channel.",
    "Video identifiers and metadata required to upload, schedule, update and manage videos.",
    "Video publishing and processing status required to show the status of operations within KGS Studio.",
    "Live broadcast and stream information required to create, configure, start, monitor and end authorized live broadcasts.",
    "OAuth authorization information, including credentials or tokens necessary to make authorized API requests on behalf of the user.",
    "Other YouTube API data strictly necessary to provide a feature that the user has explicitly enabled or authorized.",
  ];

  const purposes = [
    {
      title: "Provide KGS Studio functionality",
      description:
        "We use information to provide video uploading, scheduling, video management and live-stream functionality requested by authorized users.",
    },
    {
      title: "Perform authorized YouTube actions",
      description:
        "We use OAuth credentials and YouTube API data to perform actions on behalf of the user and only within the permissions the user has granted.",
    },
    {
      title: "Operate and maintain the service",
      description:
        "We use operational and technical information to keep KGS Studio reliable, secure and functional.",
    },
    {
      title: "Troubleshooting and support",
      description:
        "We may use logs, job status and error information to diagnose failures, investigate issues and provide support.",
    },
    {
      title: "Security and abuse prevention",
      description:
        "We may process technical and account information to protect KGS Studio, its users and connected services from unauthorized access, abuse or security incidents.",
    },
  ];

  const authorizationRules = [
    "Users explicitly authorize KGS Studio through Google's OAuth consent flow before YouTube account data requiring authorization is accessed.",
    "KGS Studio performs YouTube actions on behalf of the authenticated user and within the permissions granted by that user.",
    "KGS Studio only accesses the YouTube account or channel that the user has authorized.",
    "YouTube data is used to provide the KGS Studio features for which the user has authorized access.",
    "KGS Studio does not intentionally access unrelated YouTube accounts.",
    "Users may revoke KGS Studio's Google authorization through their Google Account permissions.",
  ];

  const dataPractices = [
    {
      title: "Data storage",
      description:
        "Information required to operate KGS Studio may be stored on our systems for as long as reasonably necessary to provide the service, maintain security, comply with legal obligations and resolve operational issues.",
    },
    {
      title: "Access controls",
      description:
        "Access to account information and YouTube authorization data is restricted to authorized systems, personnel and service providers who require access for legitimate operational purposes.",
    },
    {
      title: "Security",
      description:
        "We use reasonable administrative, technical and organizational safeguards designed to protect information against unauthorized access, loss, misuse, alteration or disclosure.",
    },
    {
      title: "Third-party services",
      description:
        "KGS Studio uses Google and YouTube services to authenticate users and provide YouTube-related functionality. Information transmitted to those services is governed by their respective policies and terms.",
    },
  ];

  const rights = [
    "Request information about personal information associated with your KGS Studio account.",
    "Request correction of inaccurate account information where applicable.",
    "Request deletion of information where applicable and subject to legal, security and operational requirements.",
    "Revoke Google/YouTube authorization granted to KGS Studio.",
    "Stop using KGS Studio if you do not wish to provide or authorize the information required for its functionality.",
  ];

  const policySections: PolicySection[] = [
    {
      number: "1",
      title: "Information we collect",
      content: (
        <div className="space-y-4">
          <p>
            KGS Studio collects information that is necessary to authenticate
            authorized users, operate the Studio, provide its video and
            live-stream functionality, maintain security and support the
            service.
          </p>

          <div className="space-y-3">
            {informationCollected.map((item) => (
              <div key={item.title} className="flex gap-3">
                <p className="m-0 leading-6">
                  <strong className="font-semibold text-[#172033]">
                    {item.title}
                  </strong>{" "}
                  — {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      ),
    },

    {
      number: "2",
      title: "KGS Studio YouTube API usage",
      content: (
        <div className="space-y-5">
          <p>
            KGS Studio uses YouTube API Services to provide authorized users
            with tools for managing YouTube videos and live broadcasts. These
            services include functionality such as uploading videos, scheduling
            publication, managing video information and managing authorized live
            broadcasts.
          </p>

          <p>
            When an authorized user connects a YouTube channel through Google's
            OAuth consent flow, KGS Studio may access YouTube API data that is
            necessary for the functionality the user has authorized.
          </p>

          <div className="space-y-3">
            {youtubeData.map((item) => (
              <div key={item} className="flex gap-3">
                <CheckCircleOutlined className="mt-1 shrink-0 text-[#00B86B]" />

                <p className="m-0 leading-6">{item}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-[#DCEFE5] bg-[#F3FBF7] p-4">
            <div className="flex gap-3">
              <YoutubeOutlined className="mt-1 shrink-0 text-[#00B86B]" />

              <p className="m-0 text-sm leading-6 text-[#475569]">
                KGS Studio uses YouTube API Services in accordance with the
                applicable YouTube API Services Terms and Developer Policies.
                YouTube's policies require API clients to clearly explain their
                use of YouTube API Services and the information accessed through
                them.
              </p>
            </div>
          </div>
        </div>
      ),
    },

    {
      number: "3",
      title: "How we use information",
      content: (
        <div className="space-y-3">
          {purposes.map((purpose) => (
            <div key={purpose.title} className="flex gap-3">
              <CheckCircleOutlined className="mt-1 shrink-0 text-[#00B86B]" />

              <p className="m-0 leading-6">
                <strong className="font-semibold text-[#172033]">
                  {purpose.title}
                </strong>{" "}
                — {purpose.description}
              </p>
            </div>
          ))}
        </div>
      ),
    },

    {
      number: "4",
      title: "Google OAuth and authorization",
      content: (
        <div className="space-y-5">
          <p>
            KGS Studio uses Google's OAuth authentication and authorization
            mechanisms when a user connects a YouTube account. Authorization is
            initiated by the user and the user is shown Google's consent
            interface before access requiring authorization is granted.
          </p>

          <div className="space-y-3">
            {authorizationRules.map((rule) => (
              <div key={rule} className="flex gap-3">
                <CheckCircleOutlined className="mt-1 shrink-0 text-[#00B86B]" />

                <p className="m-0 leading-6">{rule}</p>
              </div>
            ))}
          </div>

          <p>
            Revoking authorization may prevent KGS Studio from performing
            features that require access to the connected YouTube account.
          </p>
        </div>
      ),
    },

    {
      number: "5",
      title: "Data storage, security and access",
      content: (
        <div className="space-y-4">
          {dataPractices.map((item) => (
            <div
              key={item.title}
              className="rounded-xl border border-[#E2E5ED] bg-[#FAFBFC] p-4"
            >
              <h4 className="m-0 text-sm font-semibold text-[#172033]">
                {item.title}
              </h4>

              <p className="m-0 mt-1.5 text-sm leading-6 text-[#64748B]">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      ),
    },

    {
      number: "6",
      title: "Sharing of information",
      content: (
        <div className="space-y-4">
          <p>
            We do not sell personal information or YouTube authorization data.
          </p>

          <p>
            Information may be shared with service providers or infrastructure
            providers that help us operate, secure, maintain or support KGS
            Studio, subject to appropriate contractual and security protections.
          </p>

          <p>
            We may also disclose information when reasonably necessary to comply
            with applicable law, legal process, enforce our terms, protect the
            rights and safety of users or the service, or investigate security
            incidents or abuse.
          </p>
        </div>
      ),
    },

    {
      number: "7",
      title: "Data retention and deletion",
      content: (
        <div className="space-y-4">
          <p>
            We retain information only for as long as reasonably necessary for
            the purposes described in this Privacy Policy, including providing
            KGS Studio functionality, maintaining security, troubleshooting,
            complying with legal obligations and resolving disputes.
          </p>

          <p>
            When information is no longer required, we may delete it or securely
            de-identify it, subject to applicable legal, security and
            operational requirements.
          </p>

          <div className="rounded-xl border border-[#E2E5ED] bg-white p-4">
            <div className="flex gap-3">
              <DeleteOutlined className="mt-1 shrink-0 text-[#00B86B]" />

              <p className="m-0 text-sm leading-6 text-[#64748B]">
                If you want to request deletion of your KGS Studio account
                information or YouTube-related information maintained by KGS
                Studio, contact us using the contact information provided below.
                We may need to verify your identity before processing the
                request.
              </p>
            </div>
          </div>
        </div>
      ),
    },

    {
      number: "8",
      title: "Your choices and rights",
      content: (
        <div className="space-y-3">
          {rights.map((right) => (
            <div key={right} className="flex gap-3">
              <CheckCircleOutlined className="mt-1 shrink-0 text-[#00B86B]" />

              <p className="m-0 leading-6">{right}</p>
            </div>
          ))}
        </div>
      ),
    },

    {
      number: "9",
      title: "Cookies and technical information",
      content: (
        <div className="space-y-4">
          <p>
            KGS Studio may use browser storage, cookies or similar technical
            mechanisms where necessary to maintain authentication, remember
            application settings, improve reliability and protect the service.
          </p>

          <p>
            We may also collect technical information such as IP addresses,
            browser information, device information, request logs and error
            information for security, diagnostics and service operation.
          </p>
        </div>
      ),
    },

    {
      number: "10",
      title: "Third-party services and policies",
      content: (
        <div className="space-y-4">
          <p>
            KGS Studio integrates with Google and YouTube services. Your use of
            YouTube and Google services is also subject to the applicable Google
            and YouTube terms and privacy policies.
          </p>

          <p>
            You should review Google's Privacy Policy and YouTube's Terms of
            Service for information about how Google and YouTube handle data
            within their own services.
          </p>

          <div className="flex flex-wrap gap-3">
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-[#E2E5ED] bg-white px-4 py-2.5 text-sm font-medium text-[#1D1F5D] no-underline transition hover:border-[#00B86B]/40 hover:bg-[#F7F8FC]"
            >
              Google Privacy Policy
            </a>

            <a
              href="https://www.youtube.com/t/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-[#E2E5ED] bg-white px-4 py-2.5 text-sm font-medium text-[#1D1F5D] no-underline transition hover:border-[#00B86B]/40 hover:bg-[#F7F8FC]"
            >
              YouTube Terms of Service
            </a>
          </div>
        </div>
      ),
    },

    {
      number: "11",
      title: "Children's privacy",
      content: (
        <p>
          KGS Studio is a professional video and live-stream management tool
          intended for authorized users and team members. KGS Studio does not
          knowingly collect personal information from children for purposes
          unrelated to the operation of the service. If you believe that a child
          has provided personal information to us inappropriately, please
          contact us so that we can review and take appropriate action.
        </p>
      ),
    },

    {
      number: "12",
      title: "Changes to this Privacy Policy",
      content: (
        <p>
          We may update this Privacy Policy from time to time to reflect changes
          to KGS Studio, applicable laws, security practices or our handling of
          information. When we make material changes, we will update the date
          shown at the beginning of this policy and may provide additional
          notice where appropriate.
        </p>
      ),
    },

    {
      number: "13",
      title: "Contact us",
      content: (
        <div className="space-y-4">
          <p>
            If you have questions, concerns, complaints or requests relating to
            this Privacy Policy, KGS Studio, or the handling of your
            YouTube-related information, please contact Khan Global Studies.
          </p>

          <a
            href="mailto:enquiry@khanglobalstudies.com"
            className="inline-flex items-center gap-2 rounded-lg bg-[#00B86B] px-5 py-3 text-sm font-semibold text-white no-underline transition hover:bg-[#00995A]"
          >
            <MailOutlined />
            Contact KGS Studio
          </a>
        </div>
      ),
    },
  ];

  return (
    <main className="min-h-screen bg-[#F7F8FC]">
      <Navbar />
      <section className="border-b border-[#E2E5ED] bg-white">
        <div className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:px-10 lg:py-16">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#E8F8F0] text-[#00B86B]">
              <SafetyOutlined className="text-xl" />
            </div>

            <div>
              <div className=" flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#00A862]">
                  Legal
                </span>
              </div>

              <h1 className="m-0 text-3xl font-bold tracking-tight text-[#1D1F5D] sm:text-3xl">
                KGS Studio Privacy Policy
              </h1>

              <p className="mt-2 text-sm text-[#64748B]">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 py-10 sm:px-8 lg:px-10 lg:py-14">
        <div className="mx-auto max-w-[1100px]">
          <div className="overflow-hidden rounded-2xl border border-[#E2E5ED] bg-white shadow-[0_6px_24px_rgba(29,31,93,0.05)]">
            <div className="px-6 py-8 sm:px-10 sm:py-10 lg:px-12">
              <div className="border-b border-[#E8EAF0] pb-8">
                <p className="m-0 text-[15px] leading-7 text-[#64748B]">
                  This Privacy Policy explains how{" "}
                  <strong className="font-semibold text-[#172033]">
                    Khan Global Studies
                  </strong>{" "}
                  ("we", "us" or "our") handles information in connection with{" "}
                  <strong className="font-semibold text-[#172033]">
                    KGS Studio
                  </strong>
                  ("KGS Studio", "the application" or "the service"), a product
                  of Khan Global Studies available through kgsstudio.com.
                </p>

                <p className="mt-2 text-[15px] leading-7 text-[#64748B]">
                  KGS Studio is designed for authorized users and team members
                  who use the Studio to manage YouTube videos, schedule
                  publishing and manage authorized live broadcasts through
                  YouTube API Services.
                </p>

                <p className="mt-2 text-[15px] leading-7 text-[#64748B]">
                  This Privacy Policy supplements the general privacy practices
                  of Khan Global Studies and explains the information and
                  YouTube-specific data handling practices applicable to KGS
                  Studio.
                </p>
              </div>

              <div className="my-8 rounded-xl border border-[#DCEFE5] bg-[#F3FBF7] p-5">
                <div className="flex items-start gap-3">
                  <YoutubeOutlined className="mt-1 shrink-0  text-[#00B86B]" />

                  <div>
                    <h3 className="m-0 text-sm font-bold text-[#172033]">
                      YouTube API Services
                    </h3>

                    <p className="m-0 mt-1.5 text-sm leading-6 text-[#64748B]">
                      KGS Studio uses YouTube API Services. YouTube account
                      information and API data are accessed only after the user
                      authorizes the applicable Google OAuth permissions and are
                      used to provide KGS Studio functionality.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-10">
                {policySections.map((section) => (
                  <section key={section.number}>
                    <h2 className="text-xl font-bold tracking-tight text-[#172033] sm:text-2xl">
                      <span className="mr-2 text-[#00B86B]">
                        {section.number}.
                      </span>
                      {section.title}
                    </h2>

                    <div className="mt-2 text-sm leading-6 text-[#64748B]">
                      {section.content}
                    </div>
                  </section>
                ))}
              </div>

             
              <div className="mt-12 rounded-xl border border-[#E2E5ED] bg-[#FAFBFC] p-5">
                <div className="flex items-start gap-3">
                  <LockOutlined className="mt-1 shrink-0 text-[#1D1F5D]" />

                  <div>
                    <h3 className="m-0 text-sm font-bold text-[#172033]">
                      Our commitment to authorized access
                    </h3>

                    <p className="m-0 mt-1.5 text-sm leading-6 text-[#64748B]">
                      KGS Studio is designed to operate on behalf of users who
                      have explicitly authorized the application. We do not
                      intend to access YouTube accounts that the user has not
                      authorized.
                    </p>
                  </div>
                </div>
              </div>


              <div className="mt-10 border-t border-[#E8EAF0] pt-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="m-0 text-sm font-semibold text-[#172033]">
                      Questions about this Privacy Policy?
                    </p>

                    <p className="m-0 mt-1 text-sm text-[#64748B]">
                      Contact Khan Global Studies regarding KGS Studio.
                    </p>
                  </div>

                  <a
                    href="mailto:enquiry@khanglobalstudies.com"
                    className="inline-flex active:scale-95 w-fit items-center gap-2 rounded-lg bg-[#00B86B] px-5 py-3 text-sm font-semibold text-white no-underline transition hover:bg-[#00995A]"
                  >
                    <MailOutlined />
                    Contact us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default PrivacyPolicy;
